import React, { useEffect, useState } from "react";
import {
  Typography,
  useTheme,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Stack,
  Box,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { AssignmentSummary } from "../components/AssignmentSummary";
import StatBox from "../../../shared-components/StatBox";
import { OverallFeedback } from "../components/OverallFeedback";
import { SubmissionSection } from "../components/SubmissionSection";
import {
  calculateProgressPercent,
  formatProgressFraction,
  formatWeekDifference,
  getWeekDiffColor,
} from "../../../utils/formatters";
import {
  useExportAssignment,
  useExportAssignmentFeedback,
  useExportCoverSheets,
  useExportResults,
} from "../hooks/Exporters";
import { withPageTracking } from "../../../services/postHog";
import { QuestionViewCard } from "../../question/components/QuestionViewCard";
import { AssistantInlineCard } from "../../assistant/AssistantInlineCard";
import { AvailableQuestionTypes } from "../../question/components/QuestionTypePicker";

const AssignmentQuestionSummary = ({
  assignmentQuestion,
  multiQuestion = false,
}) => {
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <QuestionViewCard
          question={assignmentQuestion.question}
          identifier={assignmentQuestion.identifier}
          hideMarkScheme={
            assignmentQuestion.question.questionType !==
            AvailableQuestionTypes.LongText.backendName
          }
          collapsibleMenu
        />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={6}>
            <StatBox
              title="Submissions"
              stat={formatProgressFraction(
                assignmentQuestion.stats.overallSubmissions,
              )}
              footer={
                <LinearProgress
                  variant="determinate"
                  value={calculateProgressPercent(
                    assignmentQuestion.stats.overallSubmissions,
                  )}
                />
              }
            />
          </Grid>
          <Grid size={6}>
            <StatBox
              title={
                !multiQuestion ? "Assignment Percentage" : "Question Percentage"
              }
              stat={
                assignmentQuestion.stats.averageScore.current
                  ? `${assignmentQuestion.stats.averageScore.current} %`
                  : "N/A"
              }
              footer={
                !multiQuestion &&
                formatWeekDifference(assignmentQuestion.stats.averageScore)
              }
              footerColor={getWeekDiffColor(
                assignmentQuestion.stats.averageScore,
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const AssignmentQuestionQuickNav = ({ assignmentQuestion }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { assignmentId } = useParams();

  const handleNavigate = (targetId) => {
    if (targetId) {
      navigate(`/assignments/view/${assignmentId}/questions/view/${targetId}`);
    }
  };
  return (
    <Card
      sx={{
        backgroundColor: (theme.vars || theme).palette.background.default,
        mb: 2,
        mt: 2,
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant={"text"}
          startIcon={<ChevronLeft />}
          onClick={() => navigate(`/assignments/view/${assignmentId}/`)}
        >
          Back to assignment
        </Button>
        <Stack direction={"row"} spacing={2}>
          <Button
            variant={"outlined"}
            disabled={!assignmentQuestion.previousQuestionId}
            onClick={() =>
              handleNavigate(assignmentQuestion.previousQuestionId)
            }
            startIcon={<ChevronLeft />}
          >
            Previous Question
          </Button>
          <Button
            variant={"outlined"}
            disabled={!assignmentQuestion.nextQuestionId}
            onClick={() => handleNavigate(assignmentQuestion.nextQuestionId)}
            endIcon={<ChevronRight />}
          >
            Next Question
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

const AssignmentQuestionView = () => {
  const { assignmentQuestionId } = useParams();
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const { exportAssignment } = useExportAssignment();
  const { exportAssignmentFeedback } = useExportAssignmentFeedback();
  const { exportCoverSheets } = useExportCoverSheets();
  const { exportResults } = useExportResults();

  const [assignmentQuestion, setAssignmentQuestion] = useState({});
  const [answersAndIdentifiers, setAnswersAndIdentifiers] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAssignmentQuestion();
  }, [assignmentQuestionId]);

  useEffect(() => {
    const subscription = ApiClient.assignment.pollAnswersAndIdentifiers({
      assignmentQuestion: assignmentQuestionId,
      pollInterval: 5000,
      onUpdate: (data) => setAnswersAndIdentifiers(data),
    });

    return () => subscription.unsubscribe();
  }, [assignmentQuestionId]);

  const fetchAssignmentQuestion = async (retryCount = 0) => {
    const MAX_RETRIES = 3;

    setError("");
    if (retryCount === 0) setLoading(true); // Only show loader on first attempt

    try {
      const response = await ApiClient.assignment.getAssignmentQuestion({
        assignmentQuestion: assignmentQuestionId,
      });

      // Check if response exists and has the expected nested data
      if (response?.assignmentQuestion) {
        setAssignmentQuestion(response.assignmentQuestion);
      } else {
        setError(
          "We're having trouble loading this assignment. Please try refreshing.",
        );
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        console.log(
          `Signal not resolved. Retrying... (${retryCount + 1}/${MAX_RETRIES})`,
        );

        // Wait 800ms and try again
        setTimeout(() => {
          fetchAssignmentQuestion(retryCount + 1);
        }, 800);
      } else {
        console.error("Failed to fetch assignment after retries", error);
        setError(
          "We're having trouble loading this assignment. Please try refreshing.",
        );
      }
    } finally {
      if (retryCount === 0 || retryCount >= MAX_RETRIES) {
        setLoading(false);
      }
    }
  };

  const handleExportAssignment = async () => {
    await exportAssignment(assignmentQuestion.assignment);
  };

  const handleExportAllFeedback = async () => {
    await exportAssignmentFeedback(assignmentQuestion.assignment);
  };

  const handleUploadClicked = () => {
    navigate(`/assignments/submission-hub/${assignmentQuestion.assignment.id}`);
  };

  const handleDownloadCoverSheets = async () => {
    await exportCoverSheets(assignmentQuestion.assignment);
  };
  const handleExportResults = async () => {
    await exportResults(assignmentQuestion.assignment);
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!Object.keys(assignmentQuestion).length) {
    return (
      <Typography>Unable to load assignment question data. {error}</Typography>
    );
  }

  return (
    <FruitifyContainer>
      <AssignmentSummary
        assignment={assignmentQuestion.assignment}
        assignmentQuestion={assignmentQuestion}
        setAssignment={(value) =>
          setAssignmentQuestion({ ...assignmentQuestion, assignment: value })
        }
        handleExportClicked={handleExportAssignment}
        handleExportAllFeedback={handleExportAllFeedback}
        handleUploadClicked={handleUploadClicked}
        handleDownloadCoverSheets={handleDownloadCoverSheets}
        handleExportResults={handleExportResults}
        onRefresh={fetchAssignmentQuestion}
      />
      {(assignmentQuestion.nextQuestionId ||
        assignmentQuestion.previousQuestionId) && (
        <AssignmentQuestionQuickNav assignmentQuestion={assignmentQuestion} />
      )}
      <AssignmentQuestionSummary
        assignmentQuestion={assignmentQuestion}
        multiQuestion={
          assignmentQuestion.nextQuestionId ||
          assignmentQuestion.previousQuestionId
        }
      />
      <OverallFeedback
        assignmentQuestion={assignmentQuestion}
        onRefresh={fetchAssignmentQuestion}
      />
      <Box sx={{ mt: 2 }}>
        <AssistantInlineCard
          context={{
            assignment_id: assignmentQuestion.assignment.id,
            class_id: assignmentQuestion.assignment.schoolClass?.id,
          }}
          title={`Bud \u2013 ${assignmentQuestion.assignment.name}`}
        />
      </Box>
      <SubmissionSection
        assignmentQuestion={assignmentQuestion}
        identifiers={answersAndIdentifiers?.existingStudents}
        answers={answersAndIdentifiers?.answers}
        showSubmissionHubLink={true}
        submissionHubUrl={`/assignments/submission-hub/${assignmentQuestion.assignment.id}`}
      />
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentQuestionView, {
  feature: "assignment-question-new",
});
